import {
  type ApolloError,
  type FetchResult,
  gql,
  useMutation,
} from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { GET_INTEGRATION_QUERY } from "./get";

type Update = {
  integrationId: string;
  integrationName?: string | null;
  integrationConfig?: object | null;
};

export const useUpdateIntegration = (
  {
    config,
  }: {
    config?: {
      refetchQueries?: any[];
    };
  } = {
    config: {
      refetchQueries: [GET_INTEGRATION_QUERY],
    },
  },
): [
  (
    data: Update,
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<FetchResult>,
  {
    data: any;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const [updateIntegration, { data, loading, error }] = useMutation(
    UPDATE_INTEGRATION_MUTATION,
    {
      refetchQueries: config?.refetchQueries,
    },
  );

  const handleUpdate = async (
    data: Update,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    toast.dismiss();

    const toastId = toast.loading(t("generic_loading"));

    try {
      const { integrationId, ...updatedFields } = data;

      const response = await updateIntegration({
        variables: { integrationId, ...updatedFields },
      });

      if (response.errors) {
        throw new Error("error updating integration");
      }

      onSuccess();

      toast.success(t("generic_success"), { id: toastId });

      return response;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), {
        id: toastId,
        richColors: true,
      });
      throw error;
    }
  };

  return [handleUpdate, { data, loading, error }];
};

export const UPDATE_INTEGRATION_MUTATION = gql`
  mutation UpdateIntegration(
    $integrationId: String!
    $integrationName: String
    $integrationConfig: JSON
  ) {
    updateIntegrationConfig(
      integrationId: $integrationId
      integrationName: $integrationName
      integrationConfig: $integrationConfig
    ) {
      integrationId
      externalId
      integrationName
      integrationAuth
      integrationConfig
      tenantId
      createdAt
      updatedAt
    }
  }
`;
